import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, map, takeUntil } from 'rxjs';
import { MatChipsModule } from '@angular/material/chips';
import { StatisticsService } from '../../../../core/services/statistics.service';
import { WorkersService } from '../../../../core/services/workers.service';
import * as Highcharts from 'highcharts';
import moment from 'moment';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { AccountsService } from '../../../../core/services/accounts.service';
import { Workers } from '../../../../core/interfaces/workers.interface';
import { HashrateHistory, Statistics, StatisticsPeriodType } from '../../../../core/interfaces/statistics.interface';
import { Account } from '../../../../core/interfaces/accounts.interface';
import { EmptyDataComponent } from '../../../../core/components/empty-data/empty-data.component';
import { WorkersComponent } from '../../../workers/pages/workers/workers.component';
import { HighchartsChartModule } from 'highcharts-angular';
import { NgClass, NgIf, AsyncPipe } from '@angular/common';
import { CommonAccessTokenService, NotifierService, WorkersStatsChartComponent } from '@wm-libs/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  styleUrls: ['./statistics.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    NgIf,
    WorkersStatsChartComponent,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatChipsModule,
    HighchartsChartModule,
    WorkersComponent,
    EmptyDataComponent,
    AsyncPipe,
    TranslateModule,
  ],
})
export class StatisticsComponent implements OnInit, OnDestroy {
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions!: Highcharts.Options;

  isMobile$ = this.breakpointObserver.observe([Breakpoints.XSmall, Breakpoints.Small]).pipe(map((result) => result.matches));

  initStatistics: Statistics | null = null;
  statistics: Statistics | null = null;

  initStatisticsLoaded = false;
  statisticsLoaded = false;

  initWorkers: Workers | null = null;

  selectedFarm!: Account | null;
  lastDayIncome!: number;
  last24hoursIncome!: number;

  selectedPeriod: StatisticsPeriodType = '10min';

  _unsubscribe$: Subject<void> = new Subject<void>();
  isAccessTokenPage = false;

  workersSummary!: Workers | null;

  @Input() withAccessTokenWorkers = false;

  constructor(
    private breakpointObserver: BreakpointObserver,
    private statisticsService: StatisticsService,
    private workersService: WorkersService,
    private translate: TranslateService,
    private accountsService: AccountsService,
    private notifier: NotifierService,
    private accessTokenService: CommonAccessTokenService
  ) {
    this.isAccessTokenPage = this.accessTokenService.isAccessTokenPage();

    if (typeof Highcharts === 'object') {
      Highcharts.setOptions({
        time: {
          useUTC: false,
        },
      });
    }
  }

  ngOnInit(): void {
    this.accountsService.accounts$.pipe(takeUntil(this._unsubscribe$)).subscribe((accounts) => {
      if (!accounts?.length) {
        return;
      }

      for (const account of accounts) {
        if (account.current) {
          this.selectedFarm = account;
        }
      }
    });

    this.notifier.notifications$.pipe(takeUntil(this._unsubscribe$)).subscribe((result) => {
      if (result === 'POOL.FARM_SWITCHED') {
        this.notifier.clearNotifications();

        this.initStatisticsLoaded = false;

        this.initStatistics = null;
        this.selectedPeriod = '10min';
        this.getStatistics();
      }
    });

    this.workersService.initWorkers$.pipe(takeUntil(this._unsubscribe$)).subscribe((res) => {
      this.initWorkers = res;

      if (this.isAccessTokenPage) {
        this.workersSummary = res;
      }
    });

    this.translate.onLangChange.pipe(takeUntil(this._unsubscribe$)).subscribe(() => {
      this.loadChartData();
    });

    this.getStatistics();

    if (this.isAccessTokenPage && this.withAccessTokenWorkers) {
      this.workersService.getWorkers().subscribe((res) => {
        this.initWorkers = res;
        this.workersSummary = res;
      });
    }
  }

  selectPeriod(period: StatisticsPeriodType) {
    if (!period || this.selectedPeriod === period) {
      return;
    }

    this.selectedPeriod = period;
    this.getStatistics();
  }

  loadChartData() {
    this.updateHighchartsLocale();

    let hashrateData;
    let rejectRateData;

    if (this.statistics?.hashrateHistories?.length) {
      hashrateData = this.statistics?.hashrateHistories.map((data: HashrateHistory) => {
        const date = this.formatDate(data.timestamp, this.selectedPeriod);
        return [date, data.hashrate];
      });

      rejectRateData = this.statistics?.hashrateHistories.map((data: HashrateHistory) => {
        const date = this.formatDate(data.timestamp, this.selectedPeriod);
        return [date, data.rejectRate];
      });
    }

    this.chartOptions = {
      chart: {
        type: 'area',
      },
      title: {
        text: '',
      },
      xAxis: {
        type: 'datetime',
        title: {
          text: this.translate.instant('WM_TECH_POOL.STATISTICS.TIME'),
        },
      },
      yAxis: [
        {
          min: 0,
          title: {
            text: this.translate.instant('WM_TECH_POOL.STATISTICS.HASHRATE'),
          },
        },
        {
          min: 0,
          max: 100,
          title: {
            text: this.translate.instant('WM_TECH_POOL.STATISTICS.REJECT_RATE'),
          },
          opposite: true,
        },
      ],
      plotOptions: {
        series: {
          marker: {
            enabled: false,
          },
        },
      },
      series: [
        {
          name: this.translate.instant('WM_TECH_POOL.STATISTICS.HASHRATE'),
          type: 'area',
          data: hashrateData,
          fillColor: '#57d374',
          color: '#57d374',
          tooltip: {
            pointFormatter: function () {
              const hashratePoint = this.y && (this.y / 10 ** 15).toFixed(3);
              return `
                <tspan style="color: #57d374; fill: #57d374;">●</tspan>
                Hashrate: 
                <tspan style="font-weight: bold;">${hashratePoint}P</tspan>
              `;
            },
          },
        },
        {
          name: this.translate.instant('WM_TECH_POOL.STATISTICS.REJECT_RATE'),
          type: 'area',
          yAxis: 1,
          data: rejectRateData,
          color: '#ff8264',
          fillColor: '#ff8264',
          tooltip: {
            pointFormatter: function () {
              return `
                <tspan style="color: #ff8264; fill: #ff8264;">●</tspan>
                Reject Rate: 
                <tspan style="font-weight: bold;">${this.y}%</tspan>
              `;
            },
          },
        },
      ],
    };
  }

  formatDate(timestamp: number, period: string): number {
    const date = new Date(timestamp * 1000);
    return period === '10min' || period === '1hour' ? date.getTime() : moment(date).startOf('day').valueOf();
  }

  updateHighchartsLocale() {
    const months = [
      this.translate.instant('WM_TECH.MONTHS.JANUARY'),
      this.translate.instant('WM_TECH.MONTHS.FEBRUARY'),
      this.translate.instant('WM_TECH.MONTHS.MARCH'),
      this.translate.instant('WM_TECH.MONTHS.APRIL'),
      this.translate.instant('WM_TECH.MONTHS.MAY'),
      this.translate.instant('WM_TECH.MONTHS.JUNE'),
      this.translate.instant('WM_TECH.MONTHS.JULY'),
      this.translate.instant('WM_TECH.MONTHS.AUGUST'),
      this.translate.instant('WM_TECH.MONTHS.SEPTEMBER'),
      this.translate.instant('WM_TECH.MONTHS.OCTOBER'),
      this.translate.instant('WM_TECH.MONTHS.NOVEMBER'),
      this.translate.instant('WM_TECH.MONTHS.DECEMBER'),
    ];

    Highcharts.setOptions({
      lang: {
        months,
        shortMonths: months.map((month) => month.substring(0, 3)),
      },
    });
  }

  getStatistics() {
    this.statistics = null;
    this.statisticsLoaded = false;

    this.statisticsService.getStatistics(this.selectedPeriod).subscribe((res) => {
      if (this.initStatisticsLoaded === false) {
        this.initStatisticsLoaded = true;
        this.initStatistics = res;
      }

      this.statistics = res;
      this.loadChartData();

      this.statisticsLoaded = true;
    });
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }
}
